// =============================================================================
// ui/tabs/MyAccount.tsx — Profile, Security, and what is not here yet.
// -----------------------------------------------------------------------------
// ── THE SHAPE, FROM JOSH'S SCREENSHOT (2026-08-03) ──
//
// Seven cards, in this order: Profile + Security as a PAIR, then the plan card
// full width, then Realtime Sync + Download the indicator as a second pair, then
// Your Data, then Danger Zone.
//
// ⚠ This sentence used to end "This pass ports the first pair; the rest are
// named on the tab's own card" — long since false, and the not-ported card had
// drifted with it. All seven are now built (the plan card landed with #65); what
// that card still names is the full Free-vs-Pro comparison modal.
//
// ⚠ THE PAIRS ARE THE LAYOUT. The old client's comment says the subscription
// card used to sit BETWEEN Profile and Security, "which is why they could not
// pair" — so the pairing is a fix it already made, and stacking them here would
// re-introduce a bug someone else already found.
//
// ── 🚨 EVERY SECURITY ACTION RE-AUTHENTICATES FIRST, AND THAT IS NOT A FORMALITY ──
//
// Change password, change email and delete all ask for the CURRENT password
// before doing anything. The threat is a walk-up on an unlocked machine: without
// it, anyone at the keyboard can change the password and lock the owner out of
// their own trading record. Supabase's `updateUser` does NOT require the old
// password, so this check exists only because the client makes it.
//
// ⚠ It is done by signing in again with the same credentials, which quietly
// refreshes the identical session — so a correct password causes no disruption
// and a wrong one changes nothing.
//
// ── ⚠ NO PASSWORD EVER LEAVES THE HANDLER THAT READS IT ──
//
// Not into state that outlives the modal, not into an error string, not into a
// log line. The validation helpers take the values, compare them, and return a
// message about the COMPARISON — never anything derived from the value itself.
// =============================================================================

import { useState, type ReactNode } from 'react';
import { SubscriptionCard } from './SubscriptionCard';
import { useSupabase } from '../state/supabase';
import { SharedLinksCard } from '../share/SharedLinksCard';
import { useAuthUser } from '../state/useAuthUser';
import {
  formatAccountCreated, normaliseEmail, validateEmailChange, validatePasswordChange,
} from '../../selectors/accountSecurity';
import { YourDataCard } from '../components/YourDataCard';
import { RealtimeSyncCard } from './IngestKeys';
import { LoggerDownloadCard } from './LoggerDownload';
import { DangerZoneCard } from './DangerZone';
import { reportFailureAs } from '../../io/diagnostics/errorLog';

type OpenModal = 'password' | 'email' | null;

export function MyAccountTab() {
  const { phase, user, error, reload } = useAuthUser();
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (phase === 'loading') return <p className="muted">Loading your account…</p>;

  // ⚠ The error state, not a card full of blanks. This screen exists to tell
  // someone who they are signed in as; guessing at that is worse than saying so.
  if (phase === 'failed' || !user) {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your account</h3>
        <p className="muted">
          Nothing has been changed. This screen just can’t read your account
          details right now.
        </p>
        {error ? <p className="muted"><code>{error}</code></p> : null}
        <button type="button" className="btn primary" onClick={reload}>Try again</button>
      </div>
    );
  }

  return (
    // ⚠ NO "My Account" HEADING HERE. The shipping client prints the tab name
    // twice — once in the top bar, once as an in-page `<h2>` — and this client's
    // shell already names it. Repeating it would make this the only screen in
    // the app that says its own name twice (D102).
    //
    // ⚠ `.acct-tab` OWNS THE VERTICAL SPACING, as a column gap. It used to be a
    // bare div, and the two paired rows spaced themselves via `.acct-pair`'s own
    // margin while every standalone card had NONE — so Your data, Danger zone and
    // the not-ported card sat flush against each other at 0px. Exactly the defect
    // Josh reported on the Dashboard (D106), on a different tab.
    //
    // A container gap rather than a margin per card, so the NEXT card added here
    // cannot reintroduce it by forgetting one.
    <div className="acct-tab" data-testid="my-account">
      {notice ? (
        <div className="card" role="status">
          <b>{notice}</b>
        </div>
      ) : null}

      <div className="acct-pair">
        <section className="card">
          <h3>Profile</h3>
          <table className="acct-profile">
            <tbody>
              <tr>
                <td className="muted">Email</td>
                <td><b>{user.email}</b></td>
              </tr>
              <tr>
                <td className="muted">Account created</td>
                <td>{formatAccountCreated(user.createdAt)}</td>
              </tr>
              <tr>
                <td className="muted">User ID</td>
                <td>
                  <code className="acct-uid">{user.id}</code>{' '}
                  <span className="muted">— include this if you contact support</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <SecurityCard onOpen={setModal} onNotice={setNotice} />
      </div>

      {/* ⚠ THIRD AND FULL WIDTH, after the Profile/Security pair — the old
          client's position, and the destination the Pro upgrade panel's "See
          plans" button reaches. It renders NOTHING before paid launch except for
          the beta viewer, and nothing at all until the subscription read
          settles. */}
      <SubscriptionCard />

      {/* ⚠ A PAIR, like Profile and Security above. The old client injects the
          download card as a sibling of Realtime Sync into the same grid cell
          row — get the key on the left, get the file on the right. */}
      <div className="acct-pair">
        <RealtimeSyncCard />
        <LoggerDownloadCard />
      </div>

      {/* ⚠ ABOVE the export and the danger zone, and BELOW the data cards.
          The dialog that creates a link points here by name — "My Account →
          Shared links" — so the wording and the position have to agree, and a
          revoke control buried under Delete account is one nobody finds in the
          moment they want it. */}
      <SharedLinksCard />

      <YourDataCard user={user} />

      {/* ⚠ LAST, as in the old client. The export sits directly above it on
          purpose — the delete flow tells you to take one first, and the button
          is then the thing immediately above rather than a tab away. */}
      <DangerZoneCard email={user.email} />

      {/* 🔴 THE LAST ITEM ON THIS LIST SHIPPED, AND THE CARD KEPT NAMING IT.
          The Free-vs-Pro comparison is built — `ComparePlans` is rendered as a
          modal over the subscription card (`SubscriptionCard.tsx:283`), which is
          on this very screen. The comment above used to explain why it was still
          listed.

          ▶ So the card is GONE, and `NotPorted` with it, rather than left
          rendering an empty list. "Not ported yet: nothing" is a sentence with no
          reader, and a component kept for a list nobody is on is how the next
          stale entry gets added without anyone re-reading the ones above it.
          Write it again the day something on this screen is genuinely missing —
          and see `notYetCards.test.ts` first, which is what now stops one of
          these going stale unnoticed. */}

      {modal === 'password' ? (
        <ChangePasswordModal
          email={user.email}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); setNotice('Password updated.'); }}
        />
      ) : null}

      {modal === 'email' ? (
        <ChangeEmailModal
          currentEmail={user.email}
          onClose={() => setModal(null)}
          onDone={(next) => {
            setModal(null);
            setNotice(`Confirmation sent to ${next}. Click the link in it to finish — `
              + 'until then this account keeps using your current address.');
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security
// ---------------------------------------------------------------------------

function SecurityCard(
  { onOpen, onNotice }:
  { onOpen: (m: OpenModal) => void; onNotice: (s: string | null) => void },
) {
  const { sb } = useSupabase();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOutEverywhere = async () => {
    setBusy(true);
    setError(null);
    // ⚠ `scope: 'global'` is the whole feature. The default ends THIS session
    // only, which would make the button a slower version of the one in Settings
    // while claiming to reach every device.
    const { error: e } = await sb.auth.signOut({ scope: 'global' });
    if (e) {
      setBusy(false);
      setConfirming(false);
      // ⚠ `reportFailureAs` on every auth failure on this screen, so the text
      // is pinned to `.message` whatever shape comes back.
      //
      // 🔴 THE REASON THIS USED TO GIVE WAS FALSE: "Supabase rejects with its
      // OWN shape, not an `Error`, so the generic reporter records
      // `[object Object]`". Checked against the INSTALLED library 2026-08-27 —
      // `AuthError extends Error`. The belief came from this repo's own test
      // doubles, which resolve bare `{ message: '…' }` objects. Pinning is
      // still right under both shapes; it is belt-and-braces, not a rescue.
      // See #52.
      setError(reportFailureAs('sign out everywhere', e, e.message));
      return;
    }
    // No navigation: `App` watches the auth state and swaps in the sign-in
    // screen when the session ends. Routing here as well would be a second,
    // racing source of truth for the same fact.
  };

  return (
    <section className="card">
      <h3>Security</h3>
      <p className="muted">
        Each action prompts for your current password as a safety check.
      </p>

      {error ? (
        <p className="red" role="alert"><b>Still signed in everywhere.</b> {error}</p>
      ) : null}

      <div className="acct-actions">
        <button type="button" className="btn"
          onClick={() => { onNotice(null); onOpen('password'); }}>Change password</button>
        <button type="button" className="btn"
          onClick={() => { onNotice(null); onOpen('email'); }}>Change email address</button>

        {confirming ? (
          <div className="row gap-8 acct-confirm-row">
            {/* ⚠ Does NOT name the account. An earlier draft appended "using it
                for <email>", which reads as though there were more than one to
                choose between — the address is already on the card beside this
                one, and repeating it here invents a decision. */}
            <span className="muted">
              Sign out of every device, <b>including this one</b>? You’ll need to
              sign in again everywhere.
            </span>
            <span className="grow" />
            <button type="button" className="btn" disabled={busy}
              onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className="btn danger" disabled={busy}
              onClick={() => void signOutEverywhere()}>
              {busy ? 'Signing out…' : 'Yes, everywhere'}
            </button>
          </div>
        ) : (
          <button type="button" className="btn"
            onClick={() => { setError(null); setConfirming(true); }}>Sign out everywhere</button>
        )}
      </div>
    </section>
  );
}

/**
 * Re-authenticate by signing in again with the same address.
 *
 * ⚠ Supabase refreshes the SAME session when this succeeds, so a correct
 * password is invisible to the user. A wrong one changes nothing at all — this
 * is a read of "does the person at the keyboard know the password", and it has
 * no side effect either way.
 */
async function passwordIsCorrect(
  sb: ReturnType<typeof useSupabase>['sb'], email: string, password: string,
): Promise<boolean> {
  const { error } = await sb.auth.signInWithPassword({ email, password });
  return !error;
}

function ChangePasswordModal(
  { email, onClose, onDone }: { email: string; onClose: () => void; onDone: () => void },
) {
  const { sb } = useSupabase();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<{ text: string; bad: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const problem = validatePasswordChange({ current, next, confirm });
    if (problem) { setStatus({ text: problem, bad: true }); return; }

    setBusy(true);
    setStatus({ text: 'Verifying current password…', bad: false });
    if (!await passwordIsCorrect(sb, email, current)) {
      setBusy(false);
      setStatus({ text: 'Current password is incorrect.', bad: true });
      return;
    }

    setStatus({ text: 'Updating…', bad: false });
    const { error } = await sb.auth.updateUser({ password: next });
    setBusy(false);
    if (error) {
      setStatus({
        text: reportFailureAs('change password', error, error.message || 'Update failed.'),
        bad: true,
      });
      return;
    }
    onDone();
  };

  return (
    <Modal title="Change password" onClose={onClose} busy={busy} onSubmit={submit}
      submitLabel={busy ? 'Working…' : 'Update password'}>
      <p className="muted">
        Enter your current password to authorise the change, then your new
        password twice.
      </p>
      <PasswordField label="Current password" value={current} onChange={setCurrent}
        autoComplete="current-password" disabled={busy} />
      <PasswordField label="New password" value={next} onChange={setNext}
        autoComplete="new-password" disabled={busy} />
      <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm}
        autoComplete="new-password" disabled={busy} />
      <Status status={status} />
    </Modal>
  );
}

function ChangeEmailModal(
  { currentEmail, onClose, onDone }:
  { currentEmail: string; onClose: () => void; onDone: (next: string) => void },
) {
  const { sb } = useSupabase();
  const [next, setNext] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<{ text: string; bad: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const problem = validateEmailChange(next, password, currentEmail);
    if (problem) { setStatus({ text: problem, bad: true }); return; }

    // ⚠ The NORMALISED address is what gets sent and what the confirmation
    // message names — so what the user is told matches what was requested, even
    // if they typed trailing spaces or capitals.
    const target = normaliseEmail(next);

    setBusy(true);
    setStatus({ text: 'Verifying password…', bad: false });
    if (!await passwordIsCorrect(sb, currentEmail, password)) {
      setBusy(false);
      setStatus({ text: 'Password is incorrect.', bad: true });
      return;
    }

    setStatus({ text: 'Sending confirmation…', bad: false });
    const { error } = await sb.auth.updateUser({ email: target });
    setBusy(false);
    if (error) {
      setStatus({
        text: reportFailureAs('change email', error, error.message || 'Could not request change.'),
        bad: true,
      });
      return;
    }
    onDone(target);
  };

  return (
    <Modal title="Change email address" onClose={onClose} busy={busy} onSubmit={submit}
      submitLabel={busy ? 'Working…' : 'Send confirmation'}>
      <p className="muted">
        We’ll send a confirmation link to your <b>new</b> address. Until you click
        it, this account keeps using <b>{currentEmail}</b>. Your old address is
        notified too.
      </p>
      <label className="field">
        <span className="field-label">New email address</span>
        <input type="email" autoComplete="email" value={next} disabled={busy}
          onChange={(e) => setNext(e.target.value)} />
      </label>
      <PasswordField label="Current password" value={password} onChange={setPassword}
        autoComplete="current-password" disabled={busy} />
      <Status status={status} />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function PasswordField(
  { label, value, onChange, autoComplete, disabled }: {
    label: string; value: string; onChange: (v: string) => void;
    autoComplete: string; disabled?: boolean;
  },
) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {/* ⚠ `type="password"` and a real `autoComplete` token, so a password
          manager fills and saves the right box. Getting the token wrong is how a
          manager stores a NEW password over the old entry. */}
      <input type="password" autoComplete={autoComplete} value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/**
 * ⚠ `role="alert"` only when it is a PROBLEM. "Verifying…" announced as an alert
 * interrupts a screen reader mid-sentence for progress the user did not ask
 * about; a failure is exactly what should interrupt.
 */
function Status({ status }: { status: { text: string; bad: boolean } | null }) {
  if (!status) return <p className="acct-status" />;
  return (
    <p className={`acct-status${status.bad ? ' red' : ' muted'}`}
      role={status.bad ? 'alert' : 'status'}>
      {status.text}
    </p>
  );
}

function Modal(
  { title, children, onClose, onSubmit, submitLabel, busy }: {
    title: string; children: ReactNode; onClose: () => void;
    onSubmit: () => void | Promise<void>; submitLabel: string; busy?: boolean;
  },
) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal">
        <header className="modal-head">
          <b>{title}</b>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>×</button>
        </header>
        {/* ⚠ A real <form>, so Enter submits. These are three password boxes in a
            row; every password manager and every habit expects Return to work. */}
        <form
          onSubmit={(e) => { e.preventDefault(); void onSubmit(); }}
        >
          <div className="modal-body">{children}</div>
          <footer className="modal-foot">
            <span className="grow" />
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy}>{submitLabel}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}


